import axios from 'axios'
import { CamelToSnakeCaseNested } from 'shared/helpers/typeHelper'
import { BillingPeriod, ProductPrice } from 'shared/models/productSubscription'
import { parseUpcomingInvoice } from 'shared/models/subscription'

export enum ProductSubscription {
  CASH = 'CASH',
  FLEX = 'FLEX',
  FLEX_STARTER = 'FLEX_STARTER',
  PREMIUM = 'PREMIUM',
}

export enum ProductSubscriptionBundleName {
  CASH = 'CASH',
  FLEX = 'FLEX',
  PREMIUM = 'PREMIUM',
  CREDIT_ONLY = 'CREDIT_ONLY',
  FLEX_STARTER = 'FLEX_STARTER',
  FLEX_CLASSIC = 'FLEX_CLASSIC',
}

export const ProductSubscriptionBundleNames = {
  [ProductSubscriptionBundleName.CASH]: 'Cash',
  [ProductSubscriptionBundleName.FLEX]: 'Flex',
  [ProductSubscriptionBundleName.PREMIUM]: 'Virtual Carrier',
  [ProductSubscriptionBundleName.CREDIT_ONLY]: 'Credit only',
  [ProductSubscriptionBundleName.FLEX_STARTER]: 'Starter',
  [ProductSubscriptionBundleName.FLEX_CLASSIC]: '(Deprecated) Flex Classic',
}

export enum Feature {
  CREDIT = 'CREDIT',
  FACTORING_OR_INSTANT_PAY = 'FACTORING_OR_INSTANT_PAY',
  FLEX_STARTER_SEAT = 'FLEX_STARTER_SEAT',
  LOAD_BOARD = 'LOAD_BOARD',
  INSURANCE = 'INSURANCE',
}

export enum FeatureId {
  CREDIT = 1,
  FACTORING = 2,
  FLEX_STARTER_SEAT = 3,
  LOAD_BOARD = 4,
  INSURANCE = 5,
}

export const FeatureNames = {
  [FeatureId.CREDIT]: 'Credit',
  [FeatureId.FACTORING]: 'Factoring',
  [FeatureId.FLEX_STARTER_SEAT]: 'Starter',
  [FeatureId.LOAD_BOARD]: 'Load Board',
  [FeatureId.INSURANCE]: 'Insurance',
}

export const FeatureNamesMap = [
  { id: Feature.CREDIT, name: 'Credit' },
  { id: Feature.FACTORING_OR_INSTANT_PAY, name: 'Instant Pay/Factoring' },
  { id: Feature.FLEX_STARTER_SEAT, name: 'Flex Starter Seat' },
  { id: Feature.INSURANCE, name: 'VC' },
]

export enum EntitlementStatus {
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  OFFBOARDING = 'OFFBOARDING',
  REMOVED = 'REMOVED',
}

export interface Entitlement {
  name: Feature
  quantity: number
  feature_id: FeatureId
}

export function getSubscription(teamId: string) {
  const url = `/api/v1/console/teams/${teamId}/stripe/subscription/`
  return axios.get(url).then((response) => ({
    created: response.data.created ? response.data.created * 1000 : undefined,
    current_period_end: response.data.current_period_end
      ? response.data.current_period_end * 1000
      : undefined,
    trial_end: response.data.trial_end ? response.data.trial_end * 1000 : undefined,
    cancel_at: response.data.cancel_at ? response.data.cancel_at * 1000 : undefined,
    canceled_at: response.data.canceled_at ? response.data.canceled_at * 1000 : undefined,
    payment_method: response.data.payment_method,
    upcomingInvoice: parseUpcomingInvoice(response.data.upcoming_invoice),
  }))
}

export function getStarterPricing(
  teamId: string
): Promise<Omit<CamelToSnakeCaseNested<ProductPrice>, 'product'>[]> {
  const url = `/api/v1/console/teams/${teamId}/stripe/flex-starter/pricing/`
  return axios.get(url).then((response) => response.data)
}

export function changeStarterPricing({ teamId, priceId }: { teamId: string; priceId: string }) {
  const url = `/api/v1/console/teams/${teamId}/stripe/flex-starter/pricing/`
  return axios.post(url, { price_id: priceId })
}

export function createStarterPricing({
  teamId,
  amount,
  is_flat,
  billing_period,
}: {
  teamId: string
  amount: string
  is_flat: boolean
  billing_period: BillingPeriod
}) {
  const url = `/api/v1/console/teams/${teamId}/stripe/flex-starter/create-price/`
  return axios.post(url, { amount, is_flat, billing_period })
}

export function updateSubscription(teamId: string, payload: { quantity: string | number; feature: number }) {
  const url = `/api/v1/console/teams/${teamId}/stripe/subscription/`
  return axios.post(url, payload)
}

export function cancelSubscription(
  teamId: string,
  {
    refund,
    prorate,
    invoiceNow,
    cancelAtPeriodEnd,
  }: { prorate?: boolean; invoiceNow?: boolean; cancelAtPeriodEnd?: boolean; refund?: boolean }
) {
  if (cancelAtPeriodEnd) {
    const url = `/api/v1/console/teams/${teamId}/stripe/subscription/cancel-at-period-end/`
    return axios.post(url, { cancel_at_period_end: cancelAtPeriodEnd })
  }
  const url = `/api/v1/console/teams/${teamId}/stripe/subscription/cancel/`
  return axios.post(url, { prorate, invoice_now: invoiceNow, refund_unused_amount: refund })
}

export function disableCancelSubscription(teamId: string) {
  const url = `/api/v1/console/teams/${teamId}/stripe/subscription/cancel-at-period-end/`
  return axios.post(url, { cancel_at_period_end: false })
}

export function syncSubscription(teamId: string) {
  const url = `/api/v1/console/teams/${teamId}/stripe/subscription/sync/`
  return axios.post(url)
}

export function lookupSubscription(teamId: string) {
  const url = `/api/v1/console/teams/${teamId}/stripe/subscription/lookup/`
  return axios.post(url)
}
